import React, { useCallback } from 'react';
import clsx from 'clsx';

import { KEYBOARD_EVENTS } from '../../../../../../../../utils/constants/commonConstants';
import { CitationMarkerProps } from './MessagesContainer.types';
import { SOURCE_ANCHOR_PREFIX, SOURCE_HIGHLIGHT_DURATION_MS, CITATION_SOURCE_TYPE } from './MessagesContainer.constants';

import styles from './MessagesContainer.module.scss';

/**
 * Renders a clickable superscript citation marker.
 * - Resolves the emitted id to its CANONICAL id so duplicate citations of the same
 *   source share one number + anchor (Perplexity-style), and displays the
 *   reading-order number (first reference in the answer = 1).
 * - document / tool sources: colored, clickable → scroll to SourcesPanel entry.
 * - model_knowledge: renders NOTHING — model reasoning is never surfaced as a
 *   reference (product decision, 2026-07-13).
 * - No matching source (guardrail-dropped / stray sentinel): inert muted superscript.
 */
const CitationMarker: React.FC<CitationMarkerProps> = ({ id, msgId, sources, idToCanonical, displayNumbers }) => {
    const canonicalId = idToCanonical?.[id] ?? id;
    const source = sources?.find((s) => s.id === canonicalId);
    const shownNumber = displayNumbers?.[canonicalId] ?? canonicalId;

    const handleClick = useCallback(() => {
        if (!source || source.type === CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE) return;

        const anchorId = `${SOURCE_ANCHOR_PREFIX}${msgId}-${canonicalId}`;
        const el = document.getElementById(anchorId);
        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        // Briefly highlight the panel entry using the CSS module class
        el.classList.add(styles.SourceHighlight);
        setTimeout(() => {
            el.classList.remove(styles.SourceHighlight);
        }, SOURCE_HIGHLIGHT_DURATION_MS);
    }, [source, msgId, canonicalId]);

    if (!source) {
        // Inert fallback — no matching source (guardrail-dropped or stray sentinel)
        return (
            <sup className={clsx(styles.CitationMarker, styles.CitationMarkerInert)} data-testid={`citation-inert-${id}`}>
                {shownNumber}
            </sup>
        );
    }

    if (source.type === CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE) {
        // Product decision (2026-07-13): model reasoning is never surfaced as a
        // reference — no marker in the answer, no entry in the Sources panel.
        return null;
    }

    return (
        <sup
            className={clsx(
                styles.CitationMarker,
                source.type === CITATION_SOURCE_TYPE.DOCUMENT ? styles.CitationMarkerDoc : styles.CitationMarkerTool,
            )}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
                    // <sup> isn't natively interactive — stop Space from scrolling the page
                    e.preventDefault();
                    handleClick();
                }
            }}
            title={source.display_name}
            data-testid={`citation-${canonicalId}`}
        >
            {shownNumber}
        </sup>
    );
};

export default CitationMarker;
